import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { PanelSectionContent, PanelSubHeader, DefaultFlexStrechedWrapper } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import { ReportOptionCheckbox, ReportOptionDescriptionText, ReportOptionIconWrapper, ReportOptionWrapper } from '../PageHeaderStowage.style';
import { REPORT_TAB, REPORT_TYPE, REPORT_TYPE_FILE_FORMAT, ReportSubType, ReportTabConfig, ReportTabSection, ReportTabs } from 'src/utils/reportGenerator/ReportGeneratorTypes';
import LastCargoesNumberSelector from './LastCargoesNumberSelector';
import CargoNameTypeSelector from './CargoNameTypeSelector';
import ManualCargoesOnlySelector from './ManualCargoesOnlySelector';
import IncludeUnNoSelector from './IncludeUnNoSelector';
import IncludeFillRatioSelector from './IncludeFillRatioSelector';
import IncludeAllTanksSelector from './IncludeAllTanksSelector';
import Tooltip from 'src/components/ui/tooltip/Tooltip';
import ReportPanelChartererSelector from './ReportPanelChartererSelector';
import ReportPanelTankSelection from './ReportPanelTankSelection';
import ReportPanelBerthSelector from './ReportPanelBerthSelector';
import {
    ReportTypesGrid,
    ReportTypeColumn,
    NumberSelectorWrapper,
    ReportTypeTabButton,
    ReportTypeTabsWrapper,
    ReportTypeTabBadge,
    SelectedReportsStrip,
    SelectedReportsStripLabel,
    SelectedReportsStripEmptyLabel,
    SelectedReportChip,
    SelectedReportChipFormatTag,
    SelectedReportChipRemoveButton,
    ConflictBanner,
    ConflictBannerText,
    ConflictBannerButton,
    SectionDivider,
    OutputActionLabel,
    OutputActionsContainer,
    RadioInput,
} from './reportPanel.style';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';

export type OutputAction = 'Open' | 'Print' | 'Download';
export type CargoNameType = 'tradeName' | 'productName';

// Tabs for which the Charterer / Tank (and, for Berth reports, Berth) filters are applicable.
const REPORT_TABS_WITH_FILTERS: REPORT_TAB[] = [REPORT_TAB.PORT_REPORTS, REPORT_TAB.BERTH_REPORTS, REPORT_TAB.MANIFOLD_PLAN];

interface ReportOptionsSectionProps {
    groupedReportTypes: Record<REPORT_TYPE, ReportSubType[]>;
    selectedReports: Set<ReportSubType>;
    toggleReportSelection: (reportSubType: ReportSubType) => void;
    selectedReportFileType: string;
    lastCargoesNumberOfSteps: number;
    setLastCargoesNumberOfSteps: (steps: number) => void;
    selectedOutputAction: OutputAction;
    setSelectedOutputAction: (action: OutputAction) => void;
    cargoNameType?: CargoNameType;
    setCargoNameType: (type: CargoNameType) => void;
    includeUnNo: boolean;
    setIncludeUnNo: (includeUnNo: boolean) => void;
    includeFillRatio: boolean;
    setIncludeFillRatio: (includeFillRatio: boolean) => void;
    includeAllTanks: boolean;
    setIncludeAllTanks: (includeAllTanks: boolean) => void;
    manualCargoesOnly: boolean;
    setManualCargoesOnly: (manualCargoesOnly: boolean) => void;
    tanksWithManualCargoes: number[];
    selectedStowageRotationPortCall: RotationPortCall | null;
    selectedCharterers: string[];
    setSelectedCharterers: (charterers: string[]) => void;
    selectedTanks: Tank[];
    filterOnSelectedTanks: boolean;
    setFilterOnSelectedTanks: (filter: boolean) => void;
    selectedBerthReportsBerthCall: RotationBerthCall | null;
    setSelectedBerthReportsBerthCall: (berthCall: RotationBerthCall | null) => void;
    clearAllReportSelections: () => void;
}

const ReportOptionsSection: React.FC<ReportOptionsSectionProps> = ({
    groupedReportTypes,
    selectedReports,
    toggleReportSelection,
    selectedReportFileType,
    lastCargoesNumberOfSteps,
    setLastCargoesNumberOfSteps,
    selectedOutputAction,
    setSelectedOutputAction,
    cargoNameType = 'tradeName',
    setCargoNameType,
    includeUnNo,
    setIncludeUnNo,
    includeFillRatio,
    setIncludeFillRatio,
    includeAllTanks,
    setIncludeAllTanks,
    manualCargoesOnly,
    setManualCargoesOnly,
    tanksWithManualCargoes,
    selectedStowageRotationPortCall,
    selectedCharterers,
    setSelectedCharterers,
    selectedTanks,
    filterOnSelectedTanks,
    setFilterOnSelectedTanks,
    selectedBerthReportsBerthCall,
    setSelectedBerthReportsBerthCall,
    clearAllReportSelections,
}) => {
    // Auto-select Download option when xlsx is selected, and return to Open when PDF is selected
    useEffect(() => {
        if (selectedReportFileType === 'xlsx') {
            setSelectedOutputAction('Download');
        } else if (selectedReportFileType === 'pdf' && selectedOutputAction === 'Download') {
            setSelectedOutputAction('Open');
        }
    }, [selectedReportFileType, setSelectedOutputAction]);

    const handleOutputActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedOutputAction(event.target.value as OutputAction);
    };

    // Determine if xlsx type report is selected to control radio button states
    const isXlsxSelected = selectedReportFileType === 'xlsx';

    // Only show tabs whose sections actually have report subtypes available (mirrors the
    // previous behaviour of only showing REPORT_TYPE groups that had entries).
    const availableReportTabs = ReportTabs.filter((tabConfig) =>
        tabConfig.sections.some((section) =>
            (section.reportTypes ?? [section.reportType]).some((rt) => (groupedReportTypes[rt] || []).length > 0)
        )
    );

    const [activeReportTab, setActiveReportTab] = useState<REPORT_TAB | undefined>(availableReportTabs[0]?.tab);

    // Keep the active tab valid if the set of available tabs ever changes.
    useEffect(() => {
        if (!activeReportTab || !availableReportTabs.some((tabConfig) => tabConfig.tab === activeReportTab)) {
            setActiveReportTab(availableReportTabs[0]?.tab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupedReportTypes]);

    const activeTabConfig: ReportTabConfig | undefined = ReportTabs.find((tabConfig) => tabConfig.tab === activeReportTab);
    const activeTabHasFilters = activeTabConfig ? REPORT_TABS_WITH_FILTERS.includes(activeTabConfig.tab) : false;
    const activeTabRequiresBerthSelection = !!activeTabConfig?.requiresBerthSelection;
    const isBerthMissingForActiveTab = activeTabRequiresBerthSelection && !selectedBerthReportsBerthCall;

    // Resolves the subtypes to render for a given tab section, filtered by reportScope when the
    // section specifies one (Port/Berth reports sections); sections without a reportScope
    // (Manifold plan, Last cargoes) render every subtype for that report type. When a section
    // lists multiple `reportTypes` (e.g. the FOSFA column combining Approved and NOBL), subtypes
    // from all of them are concatenated in declaration order.
    const getSubTypesForSection = (section: ReportTabSection): ReportSubType[] =>
        (section.reportTypes ?? [section.reportType]).flatMap((rt) =>
            (groupedReportTypes[rt] || []).filter((option) => option.reportScope === section.reportScope)
        );

    // All subtypes across every section of a tab - used for the tab badge count and the
    // PDF/Excel format-conflict check, which apply at the whole-tab level.
    const getSubTypesForTab = (tabConfig: ReportTabConfig): ReportSubType[] =>
        tabConfig.sections.flatMap((section) => getSubTypesForSection(section));

    const getSelectedCountForTab = (tabConfig: ReportTabConfig): number => {
        const tabSubTypes = getSubTypesForTab(tabConfig);
        return Array.from(selectedReports).filter((report) => tabSubTypes.includes(report)).length;
    };

    // The active tab is entirely blocked when every one of its sub-types is disabled purely because
    // a report of the other file format (PDF vs Excel) is already selected elsewhere.
    const activeTabSubTypes = activeTabConfig ? getSubTypesForTab(activeTabConfig) : [];
    const isActiveTabFullyBlockedByFormatConflict =
        !!activeTabConfig &&
        activeTabSubTypes.length > 0 &&
        selectedReportFileType !== null &&
        getSelectedCountForTab(activeTabConfig) === 0 &&
        activeTabSubTypes.every((option) => REPORT_TYPE_FILE_FORMAT[option.reportType] !== selectedReportFileType);

    // Explains why a report sub-type checkbox is disabled, so the "not-allowed" cursor isn't a dead end for the user.
    const getReportOptionDisabledReason = (option: ReportSubType): string | undefined => {
        if (selectedReports.has(option)) return undefined;

        if (isBerthMissingForActiveTab) {
            return 'Select a berth above to enable this report.';
        }

        if (selectedReportFileType === null) return undefined;

        const optionFormat = REPORT_TYPE_FILE_FORMAT[option.reportType];

        if (selectedReportFileType !== optionFormat) {
            return `Can't combine ${optionFormat.toUpperCase()} reports with the ${selectedReportFileType.toUpperCase()} report(s) already selected. Deselect those first to choose this report.`;
        }

        if (selectedReportFileType === 'xlsx') {
            return 'Only one Excel report can be generated at a time. Deselect your current Excel report selection first.';
        }

        return undefined;
    };

    // Last Cargoes, Last Cargoes - FOSFA (Approved), and Last Cargoes - NOBL share the same
    // "number of steps" / manual-cargoes / cargo-name-type / UN-No controls, and enabling any of
    // these subtypes should enable those shared controls. The "include all tanks" control applies
    // to either FOSFA-family subtype (Approved or NOBL).
    const lastCargoesSubTypes = [
        ...(groupedReportTypes[REPORT_TYPE.LAST_CARGOES] || []),
        ...(groupedReportTypes[REPORT_TYPE.LAST_CARGOES_FOSFA] || []),
        ...(groupedReportTypes[REPORT_TYPE.LAST_CARGOES_NOBL] || []),
    ];
    const isAnyLastCargoesSelected = lastCargoesSubTypes.some((st) => selectedReports.has(st));
    const isFosfaLastCargoesSelected = lastCargoesSubTypes.some(
        (st) =>
            (st.reportType === REPORT_TYPE.LAST_CARGOES_FOSFA || st.reportType === REPORT_TYPE.LAST_CARGOES_NOBL) && selectedReports.has(st)
    );
    // "Include fill ratio" only applies to the plain Last Cargoes (3LC) report - it's mandatory
    // (and always shown) for the FOSFA/NOBL variants, so the checkbox is only clickable when
    // the plain Last Cargoes report is selected.
    const isPlainLastCargoesSelected = (groupedReportTypes[REPORT_TYPE.LAST_CARGOES] || []).some((st) => selectedReports.has(st));

    const renderReportOption = (option: ReportSubType) => (
        <ReportOption
            key={option.subType}
            option={option}
            isSelected={selectedReports.has(option)}
            toggleSelection={toggleReportSelection}
            isDisabled={
                (isBerthMissingForActiveTab && !selectedReports.has(option)) ||
                (selectedReportFileType !== null && selectedReportFileType !== REPORT_TYPE_FILE_FORMAT[option.reportType]) ||
                (selectedReportFileType === 'xlsx' && !selectedReports.has(option))
            }
            disabledReason={getReportOptionDisabledReason(option)}
        />
    );

    // Last Cargoes and Last Cargoes - FOSFA share these controls (number of steps, manual cargoes
    // only, cargo name type, UN No). Rendered once below their grid (rather than duplicated per
    // report sub-type), using the same two-column grid so "Include all tanks" - which only
    // applies to the FOSFA report - lines up on the right, under the FOSFA column above.
    const renderLastCargoesSharedControls = () => {
        const selectALastCargoesReportMessage = 'Select a "Last Cargoes" report above to enable this option.';

        return (
            <div>
                <SectionDivider />
                <PanelSubHeader noBorder>Options</PanelSubHeader>
                <ReportTypesGrid>
                    <ReportTypeColumn>
                        <Tooltip text={selectALastCargoesReportMessage} type="information" disabled={isAnyLastCargoesSelected}>
                            <NumberSelectorWrapper disabled={!isAnyLastCargoesSelected}>
                                <LastCargoesNumberSelector
                                    lastCargoesNumberOfSteps={lastCargoesNumberOfSteps}
                                    setLastCargoesNumberOfSteps={setLastCargoesNumberOfSteps}
                                    disabled={!isAnyLastCargoesSelected}
                                />
                            </NumberSelectorWrapper>
                        </Tooltip>
                        <Tooltip
                            text={
                                !isAnyLastCargoesSelected
                                    ? selectALastCargoesReportMessage
                                    : tanksWithManualCargoes.length === 0
                                    ? `There are no manually inserted cargoes for this call: ${
                                          selectedStowageRotationPortCall?.portDisplayName || ''
                                      }.`
                                    : `When selected, only manually inserted cargoes at ${
                                          selectedStowageRotationPortCall?.portDisplayName || ''
                                      } will be included in the report.`
                            }
                            type="information"
                        >
                            <ManualCargoesOnlySelector
                                manualCargoesOnly={manualCargoesOnly}
                                setManualCargoesOnly={setManualCargoesOnly}
                                disabled={!isAnyLastCargoesSelected || !tanksWithManualCargoes.length}
                            />
                        </Tooltip>
                        <Tooltip text={selectALastCargoesReportMessage} type="information" disabled={isAnyLastCargoesSelected}>
                            <CargoNameTypeSelector
                                cargoNameType={cargoNameType}
                                setCargoNameType={setCargoNameType}
                                disabled={!isAnyLastCargoesSelected}
                            />
                        </Tooltip>
                        <Tooltip text={selectALastCargoesReportMessage} type="information" disabled={isAnyLastCargoesSelected}>
                            <IncludeUnNoSelector includeUnNo={includeUnNo} setIncludeUnNo={setIncludeUnNo} disabled={!isAnyLastCargoesSelected} />
                        </Tooltip>
                        <Tooltip
                            text={
                                isPlainLastCargoesSelected
                                    ? undefined
                                    : isFosfaLastCargoesSelected
                                    ? 'Fill ratio is always included in the FOSFA / NOBL reports.'
                                    : selectALastCargoesReportMessage
                            }
                            type="information"
                            disabled={isPlainLastCargoesSelected}
                        >
                            <IncludeFillRatioSelector
                                includeFillRatio={isPlainLastCargoesSelected ? includeFillRatio : isFosfaLastCargoesSelected}
                                setIncludeFillRatio={setIncludeFillRatio}
                                disabled={!isPlainLastCargoesSelected}
                            />
                        </Tooltip>
                    </ReportTypeColumn>
                    <ReportTypeColumn>
                        <Tooltip
                            text="By default, only tanks whose immediate previous cargo passes the FOSFA test (Approved) or isn't on the banned list (NOBL) are included; select this to include all tanks."
                            type="information"
                            disabled={!isFosfaLastCargoesSelected}
                        >
                            <IncludeAllTanksSelector
                                includeAllTanks={includeAllTanks}
                                setIncludeAllTanks={setIncludeAllTanks}
                                disabled={!isFosfaLastCargoesSelected}
                            />
                        </Tooltip>
                    </ReportTypeColumn>
                </ReportTypesGrid>
            </div>
        );
    };

    const renderActiveTab = () => {
        if (!activeTabConfig) return null;

        const renderSection = (section: ReportTabSection) => {
            const subTypes = getSubTypesForSection(section);
            if (subTypes.length === 0) return null;

            return (
                <ReportTypeColumn key={`${activeTabConfig.tab}-${section.reportType}-${section.reportScope || ''}`}>
                    <PanelSubHeader noBorder>{section.header ?? activeTabConfig.title}</PanelSubHeader>
                    {subTypes.map((option) => renderReportOption(option))}
                </ReportTypeColumn>
            );
        };

        return (
            <PanelSectionContent key={activeTabConfig.tab}>
                {activeTabConfig.requiresBerthSelection && (
                    <ReportPanelBerthSelector
                        selectedStowageRotationPortCall={selectedStowageRotationPortCall}
                        selectedBerthReportsBerthCall={selectedBerthReportsBerthCall}
                        setSelectedBerthReportsBerthCall={setSelectedBerthReportsBerthCall}
                    />
                )}

                {activeTabConfig.sections.length > 1 ? (
                    <ReportTypesGrid>{activeTabConfig.sections.map((section) => renderSection(section))}</ReportTypesGrid>
                ) : (
                    activeTabConfig.sections.map((section) => renderSection(section))
                )}

                {activeTabConfig.tab === REPORT_TAB.LAST_CARGOES && renderLastCargoesSharedControls()}
            </PanelSectionContent>
        );
    };

    return (
        <>
            <PanelSectionContent>
                <ReportTypeTabsWrapper>
                    {availableReportTabs.map((tabConfig) => {
                        const selectedCount = getSelectedCountForTab(tabConfig);

                        return (
                            <ReportTypeTabButton
                                key={tabConfig.tab}
                                type="button"
                                isSelected={activeReportTab === tabConfig.tab}
                                onClick={() => setActiveReportTab(tabConfig.tab)}
                            >
                                {tabConfig.title}
                                {selectedCount > 0 && <ReportTypeTabBadge>{selectedCount}</ReportTypeTabBadge>}
                            </ReportTypeTabButton>
                        );
                    })}
                </ReportTypeTabsWrapper>

                {renderActiveTab()}

                {activeTabHasFilters && (
                    <PanelSectionContent>
                        <PanelSubHeader>Filters</PanelSubHeader>

                        <DefaultFlexStrechedWrapper flexWidth="48%" noMargin alignTop className="highlight-panel">
                            <ReportPanelChartererSelector selectedCharterers={selectedCharterers} setSelectedCharterers={setSelectedCharterers} />
                            <ReportPanelTankSelection
                                selectedTanks={selectedTanks}
                                filterOnSelectedTanks={filterOnSelectedTanks}
                                setFilterOnSelectedTanks={setFilterOnSelectedTanks}
                                chartererFilterActive={selectedCharterers.length > 0}
                            />
                        </DefaultFlexStrechedWrapper>
                    </PanelSectionContent>
                )}
            </PanelSectionContent>

            <PanelSectionContent>
                <SectionDivider />

                <SelectedReportsStrip>
                    <SelectedReportsStripLabel>Selected reports</SelectedReportsStripLabel>
                    {selectedReports.size === 0 ? (
                        <SelectedReportsStripEmptyLabel>No reports selected</SelectedReportsStripEmptyLabel>
                    ) : (
                        Array.from(selectedReports).map((option) => {
                            const format = REPORT_TYPE_FILE_FORMAT[option.reportType];
                            const label =
                                option.reportType === REPORT_TYPE.LAST_CARGOES
                                    ? `${option.subType} \u2013 last ${lastCargoesNumberOfSteps} cargoes`
                                    : option.subType;

                            return (
                                <SelectedReportChip key={option.subType}>
                                    <SelectedReportChipFormatTag format={format}>
                                        {format === 'pdf' ? 'PDF' : 'Excel'}
                                    </SelectedReportChipFormatTag>
                                    {label}
                                    <SelectedReportChipRemoveButton
                                        type="button"
                                        onClick={() => toggleReportSelection(option)}
                                        aria-label={`Remove ${option.subType}`}
                                        title={`Remove ${option.subType}`}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </SelectedReportChipRemoveButton>
                                </SelectedReportChip>
                            );
                        })
                    )}
                </SelectedReportsStrip>

                {isActiveTabFullyBlockedByFormatConflict && (
                    <ConflictBanner>
                        <ConflictBannerText>
                            {selectedReportFileType === 'xlsx'
                                ? "This tab's reports are PDF, but you currently have an Excel report selected. PDF and Excel reports can't be generated together."
                                : "This tab's reports are Excel, but you currently have PDF report(s) selected. PDF and Excel reports can't be generated together."}
                        </ConflictBannerText>
                        <ConflictBannerButton type="button" onClick={clearAllReportSelections}>
                            Clear selection
                        </ConflictBannerButton>
                    </ConflictBanner>
                )}

                <OutputActionsContainer>
                    <OutputActionLabel disabled={isXlsxSelected}>
                        <RadioInput
                            type="radio"
                            name="outputAction"
                            value="Open"
                            checked={selectedOutputAction === 'Open'}
                            onChange={handleOutputActionChange}
                            disabled={isXlsxSelected}
                        />
                        Open
                    </OutputActionLabel>
                    <OutputActionLabel disabled={isXlsxSelected}>
                        <RadioInput
                            type="radio"
                            name="outputAction"
                            value="Print"
                            checked={selectedOutputAction === 'Print'}
                            onChange={handleOutputActionChange}
                            disabled={isXlsxSelected}
                        />
                        Print
                    </OutputActionLabel>
                    <OutputActionLabel>
                        <RadioInput
                            type="radio"
                            name="outputAction"
                            value="Download"
                            checked={selectedOutputAction === 'Download' || isXlsxSelected}
                            onChange={handleOutputActionChange}
                        />
                        Download
                    </OutputActionLabel>
                </OutputActionsContainer>
            </PanelSectionContent>
        </>
    );
};

interface ReportOptionProps {
    option: ReportSubType;
    isSelected: boolean;
    toggleSelection: (option: ReportSubType) => void;
    isDisabled: boolean;
    disabledReason?: string;
}

const ReportOption: React.FC<ReportOptionProps> = ({ option, isSelected, toggleSelection, isDisabled, disabledReason }) => {
    const optionContent = (
        <ReportOptionWrapper onClick={() => !isDisabled && toggleSelection(option)} disabled={isDisabled}>
            <DefaultFlexStrechedWrapper noMargin>
                <ReportOptionIconWrapper>
                    <FontAwesomeIcon className="icon" icon={option.icon} />
                    <ReportOptionCheckbox
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(option)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={isDisabled}
                        aria-label={`Select ${option.subType}`}
                    />
                </ReportOptionIconWrapper>
                <ReportOptionDescriptionText>{option.subType}</ReportOptionDescriptionText>
            </DefaultFlexStrechedWrapper>
        </ReportOptionWrapper>
    );

    if (!isDisabled || !disabledReason) {
        return optionContent;
    }

    return (
        <Tooltip text={disabledReason} type="information">
            {optionContent}
        </Tooltip>
    );
};

export default ReportOptionsSection;

